import React, { useState } from "react";
import { ThemeProvider } from "styled-components";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import theme from "onnit-ui/lib/themes/light";
import Box from "onnit-ui/lib/box/Box";
import Text, { List, ListItem } from "onnit-ui/lib/text";
import { Img, Picture } from "onnit-ui/lib/image";
import TwelveColumn from "onnit-ui/lib/module/grid/12/Grid";
import config from "../../config/config";
import Embed from "onnit-ui/lib/video/Embed";
import Sup from "onnit-ui/lib/text/Sup";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";
import usePageData from "../../hooks/usePageData";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";

import CopyDisplay02 from "onnit-ui/lib/module/CopyDisplay02";
import GradientText from "onnit-ui/lib/text/GradientText";
import ingredients from "./ingredients";
import MediaContentShowcase, { MediaContentShowcaseItem } from "onnit-ui/lib/module/MediaContentShowcase";
import SimpleBuyBox from "onnit-ui/lib/buybox/simple/SimpleBuyBox";
import Product from "onnit-ui/lib/@types/interfaces/product/BuyBoxProduct";
import CustomPackageBuyBox from "onnit-ui/lib/buybox/simple/CustomPackageBuyBox";

function transformProductName(name: string): string {
    return name.replace(/Alpha Brain® Pre-Workout - /gi, "");
}

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData, findProduct } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) return null;

    const IMG_URL = onnitContext.url.cdn.static_images + "/images/product-page/alpha-brain-pre-workout";
    const { IMG_PARAMS } = config;

    const peachId = 9124;
    const tigerId = 9125;
    const creatine = findProduct(5587);
    const shroomTechSport = findProduct(4448);
    const totalNo = findProduct(6668);

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    productNameTransform={transformProductName}
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid variant="layout3">
                        <Img
                            src={`${IMG_URL}/buybox_2_1.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="tub of alpha brain pre workout tiger's blood flavor"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/buybox_3_1.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="tub of alpha brain pre workout yuzu peach flavor"
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/buybox_4-mobile.png${IMG_PARAMS}`,
                                `${IMG_URL}/buybox_4.png${IMG_PARAMS}`,
                            ]}
                            alt="woman benching dumbbells"
                        />
                        <Img
                            src={`${IMG_URL}/buybox_5.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="man swinging kettlebell"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/buybox_1.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="woman kicking heavy bag"
                            width={920}
                            height={920}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box bg="grays.7" py={[6, 6, 6, 8]}>
                        <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                            <Box gridColumn="2 / span 7" px={4}>
                                <Text as="h2" color="grays.3" typeStyle="text02" textTransform="uppercase">
                                    Alpha BRAIN
                                    <Sup variant="reg" /> PRE-Workout
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                            <Box gridColumn="7 / span 5" px={4}>
                                <Embed
                                    title="Alpha BRAIN Pre-Workout video"
                                    src="https://player.vimeo.com/video/819686782"
                                />
                            </Box>
                            <Box
                                gridColumn="2 / span 5"
                                display={["block", "block", "flex"]}
                                alignItems="center"
                                px={4}
                            >
                                <Text
                                    display={["block", "block", "none"]}
                                    as="h2"
                                    color="grays.3"
                                    typeStyle="text02"
                                    textTransform="uppercase"
                                    my={3}
                                >
                                    Alpha BRAIN
                                    <Sup variant="reg" /> PRE-Workout
                                </Text>

                                <GradientText as="p" typeStyle="title01" startColor="#E3AB31" endColor="#E1752D" mb={2}>
                                    Go Hard. <Box as="br" display={["none", "none", "block"]} />
                                    Go Long. <Box as="br" display={["none", "none", "block"]} />
                                    Stay Focused.
                                    <Sup style={{ fontSize: 12, verticalAlign: "top" }} />
                                </GradientText>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box gridColumn="2 / span 7" px={4} mb={[6, 6, 6, 8]}>
                                <Text as="p" color="white" typeStyle="text02" mb={4}>
                                    No more "meh" workouts. The combination of caffeine, amino acids, and other
                                    compounds in Alpha BRAIN
                                    <Sup variant="reg" /> Pre-workout supports the mental focus and physical energy you
                                    need to make every session count.
                                    <Sup />
                                </Text>
                                <Text as="p" color="white" typeStyle="text02" fontWeight="black">
                                    Sweetener from natural sources. NO synthetic colors or dyes.
                                </Text>
                            </Box>
                            <Box gridColumn="3 / span 7" px={4}>
                                <GradientText as="p" typeStyle="title01" startColor="#E3AB31" endColor="#E1752D" mb={4}>
                                    Alpha BRAIN
                                    <Sup variant="reg" /> <Box as="br" display={["none", "none", "block"]} />
                                    PRE-Workout is for:
                                </GradientText>
                                <List>
                                    <ListItem typeStyle="text02" color="white">
                                        Athletes
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="white">
                                        Recreational gym goers
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="white">
                                        People who train early a.m.
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="white">
                                        Anyone who wants to feel more focus during training
                                        <Sup />
                                    </ListItem>
                                </List>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <Box py={[6, 6, 6, 8]}>
                        <Text typeStyle="title01" textAlign="center" mb={4}>
                            Available in two flavors
                        </Text>
                        <Box display="flex" justifyContent="center">
                            {pageData.products.map((product: Product, index) => (
                                <Box maxWidth="364px" px={[4, 4, 6]} key={index}>
                                    <SimpleBuyBox
                                        product={product}
                                        additionalImgixParams="crop=bottom,center&fit=crop&h=0.35"
                                        productNameTransform={transformProductName}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box bg="grays.7" py={[4, 4, 4, 6]}>
                        <CopyDisplay02 py={4} dashColor="#E3AB31" dashEndColor="#E1752D">
                            <Text typeStyle="title02" color="white" mb={4}>
                                Can I Stack Alpha BRAIN
                                <Sup variant="reg" /> Pre-Workout With Other Onnit Products?
                            </Text>
                            <Text as="p" typeStyle="text03" color="white">
                                Yes! Alpha BRAIN
                                <Sup variant="reg" aria-hidden="true" /> Pre-Workout can provide additional exercise
                                support when paired with Creatine, Shroom Tech
                                <Sup variant="reg" aria-hidden="true" /> SPORT, Total Nitric Oxide
                                <Sup variant="reg" aria-hidden="true" />, Alpha BRAIN
                                <Sup variant="reg" aria-hidden="true" /> Instant, Alpha BRAIN
                                <Sup variant="reg" aria-hidden="true" /> Black Label and Alpha BRAIN
                                <Sup variant="reg" aria-hidden="true" /> Focus Shot.
                                <Sup />
                            </Text>
                        </CopyDisplay02>
                    </Box>

                    <Box
                        py={[6, 6, 6, 8]}
                        display={["block", "block", "grid"]}
                        gridTemplateColumns="1fr 1fr"
                        maxWidth="1040px"
                        mx="auto"
                    >
                        <CustomPackageBuyBox
                            imgAltText="Alpha Brain Pre Workout and Creatine."
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Pre-Workout + Creatine
                                </>
                            }
                            selectableProducts={pageData.products}
                            packageProducts={[creatine] as Product[]}
                            images={{
                                [peachId]: `${IMG_URL}/yp-creatine.png${IMG_PARAMS}`,
                                [tigerId]: `${IMG_URL}/tb-creatine.png${IMG_PARAMS}`,
                            }}
                            productNameTransform={transformProductName}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Promotes strength, power, and anaerobic endurance performance.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>

                        <CustomPackageBuyBox
                            imgAltText="Alpha Brain Pre Workout and Shroom Tech Sport."
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Pre-Workout + Shroom Tech
                                    <Sup variant="reg" /> SPORT
                                </>
                            }
                            selectableProducts={pageData.products}
                            packageProducts={[shroomTechSport] as Product[]}
                            images={{
                                [peachId]: `${IMG_URL}/yp-STS.png${IMG_PARAMS}`,
                                [tigerId]: `${IMG_URL}/tb-STS.png${IMG_PARAMS}`,
                            }}
                            productNameTransform={transformProductName}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Supports aerobic endurance and exercise volume.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>

                        <CustomPackageBuyBox
                            imgAltText="Alpha Brain Pre Workout and Total Nitric Oxide."
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Pre-Workout + Total Nitric Oxide
                                    <Sup variant="reg" />
                                </>
                            }
                            selectableProducts={pageData.products}
                            packageProducts={[totalNo] as Product[]}
                            images={{
                                [peachId]: `${IMG_URL}/yp-TNO.png${IMG_PARAMS}`,
                                [tigerId]: `${IMG_URL}/tb-TNO.png${IMG_PARAMS}`,
                            }}
                            productNameTransform={transformProductName}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Assists with blood flow and post-workout muscle recovery.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>

                        <CustomPackageBuyBox
                            imgAltText="Alpha Brain Pre Workout, Creatine and Total Nitric Oxide."
                            gridColumn="span 1"
                            my={4}
                            px={7}
                            name="Combine All Four for the ultimate pre-workout stack!"
                            selectableProducts={pageData.products}
                            packageProducts={[shroomTechSport, creatine, totalNo] as Product[]}
                            images={{
                                [peachId]: `${IMG_URL}/yp-ultimate.png${IMG_PARAMS}`,
                                [tigerId]: `${IMG_URL}/tb-ultimate.png${IMG_PARAMS}`,
                            }}
                            productNameTransform={transformProductName}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Pre-Workout
                                </ListItem>
                                <ListItem color="grays.5">Creatine</ListItem>
                                <ListItem color="grays.5">
                                    Shroom Tech
                                    <Sup variant="reg" /> SPORT
                                </ListItem>
                                <ListItem color="grays.5">
                                    Total Nitric Oxide
                                    <Sup variant="reg" />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>
                    </Box>

                    <Box bg="grays.7" py={[6, 6, 6, 8]}>
                        <TwelveColumn>
                            <Box px={4} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                <Text as="h2" typeStyle="title02" color="white">
                                    Key Ingredients
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <MediaContentShowcase
                            items={ingredients.map((x: MediaContentShowcaseItem) => {
                                let copy = { ...x };
                                copy.image = `${IMG_URL}/${x.image}`;
                                return copy;
                            })}
                            textColor="white"
                            activeColor="#E3AB31"
                            bgMedia={
                                <Img
                                    src={`${IMG_URL}/ingredients-background.png${IMG_PARAMS}`}
                                    alt=""
                                    aria-hidden="true"
                                    width={1848}
                                    height={518}
                                />
                            }
                        />
                    </Box>
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers supplementFacts={<SupplementFacts currentPID={currentPID} />}>
                        <Text typeStyle="caption02" color="grays.5" mb={4}>
                            <b>WARNING:</b> Consult a medical doctor before taking this supplement if you have or
                            suspect a medical condition or are taking any medications. Contains approximately the same
                            amount of caffeine as two cups of coffee (200mg). Do not use near bedtime, if you're
                            pregnant, nursing, or are sensitive to caffeine. Discontinue taking this product if you
                            experience an adverse event.{" "}
                            <b>
                                KEEP OUT OF REACH OF CHILDREN. Store in a cool, dry place. Do not use this product if
                                the freshness seal is missing or broken.
                            </b>
                        </Text>
                        <Text typeStyle="caption02" color="grays.5">
                            PFBc
                            <Sup variant="reg" /> is a registered trademark of Phenolaeis USA. NooGandha
                            <Sup variant="reg" /> is a registered trademark of Specnova
                            <Sup variant="reg" />, LLC. AlphaSize
                            <Sup variant="reg" /> is a registered trademark of Chemi Nutra. Dynamine
                            <Sup variant="trade" /> is a trademark and protected by Patents Pending claiming priority to
                            Serial No. 61/903,362; under exclusive global distribution by Compound Solutions, Inc.
                        </Text>
                    </UsageAndDisclaimers>
                )}
            </>
        </ThemeProvider>
    );
}

export default Page;
